import { useEffect, useState } from "react";
import { http } from "../api";
import { PageHeader, EmptyState } from "../shared";
import { AlertCircle, ArrowRight, Users as UsersIcon, GitMerge, Eye, Check, ArrowLeft } from "lucide-react";

export default function Duplicates({ user }) {
  const isSuper = user?.role === "Super Admin";
  const [data, setData] = useState({ count: 0, duplicates: [] });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // {mobile, profiles}
  const [winnerIdx, setWinnerIdx] = useState(0);

  const [preview, setPreview] = useState(null); // {winner, loser, diff, reassign_counts, choices}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await http.get("/duplicates"); setData(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  if (!isSuper) {
    return (
      <>
        <PageHeader eyebrow="DUPLICATES" title="Duplicate profiles" subtitle="Super Admin only" />
        <EmptyState icon={UsersIcon} title="Super Admin access only" hint="Ask a Super Admin to merge duplicate profiles." />
      </>
    );
  }

  const openPreview = async (mobile, winner, loser) => {
    setBusy(true);
    try {
      const r = await http.post("/duplicates/merge-preview", {
        winner_kind: winner.kind, winner_id: winner.id,
        loser_kind: loser.kind, loser_id: loser.id,
      });
      const choices = {};
      (r.data.diff || []).forEach((d) => { choices[d.field] = d.default_choice; });
      setPreview({ ...r.data, mobile, winner, loser, choices });
      setActive(null);
    } catch (e) { window.alert(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const doMerge = async () => {
    setBusy(true);
    try {
      await http.post("/duplicates/merge-v2", {
        mobile: preview.mobile,
        winner_kind: preview.winner.kind, winner_id: preview.winner.id,
        loser_kind: preview.loser.kind, loser_id: preview.loser.id,
        field_choices: preview.choices || {},
      });
      setPreview(null);
      load();
    } catch (e) { window.alert(e.response?.data?.detail || "Merge failed"); }
    finally { setBusy(false); }
  };

  const renderVal = (v) => {
    if (v === null || v === undefined || v === "") return <em style={{ opacity: .5 }}>— empty —</em>;
    if (Array.isArray(v)) return v.length ? v.join(", ") : <em style={{ opacity: .5 }}>— empty —</em>;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  return (
    <>
      <PageHeader eyebrow="GOVERNANCE" title="Duplicate profiles"
        subtitle="Profiles that share the same mobile number. Preview the field diff, pick which values to keep, and merge. The loser is moved to Recycle bin."
        actions={<button className="outline" data-testid="dup-refresh" onClick={load}>Refresh</button>} />

      <section className="panel">
        {loading ? <p className="hint" style={{ padding: 30, textAlign: "center" }}>Scanning…</p>
        : data.count === 0 ? <EmptyState icon={UsersIcon} title="No duplicate profiles" hint="Every mobile number is unique across contacts + public users. 🎉" />
        : (
          <ul className="dup-list" data-testid="dup-list">
            {data.duplicates.map((d) => (
              <li key={d.mobile} className="dup-row" data-testid={`dup-row-${d.mobile}`}>
                <div className="dup-body">
                  <b><AlertCircle size={14} /> {d.mobile}</b>
                  <div className="dup-chips">
                    {d.profiles.map((p) => (
                      <span key={`${p.kind}-${p.id}`} className={`dup-chip dup-chip-${p.kind}`}>
                        <b>{p.name}</b><small>{p.kind === "contact" ? "Voter" : "Public"}</small>
                      </span>
                    ))}
                  </div>
                  <small>{d.profiles.length} profiles sharing this number</small>
                </div>
                <button className="primary" data-testid={`dup-review-${d.mobile}`} onClick={() => { setActive(d); setWinnerIdx(0); }}>
                  <GitMerge size={13} /> Review & merge
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Step 1: pick winner */}
      {active && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActive(null); }}>
          <div className="dup-drawer" data-testid="dup-drawer">
            <div className="bo-drawer-head">
              <b>Step 1: Pick the winning profile · {active.mobile}</b>
              <button className="icon-btn" data-testid="dup-close" onClick={() => setActive(null)}>×</button>
            </div>
            <p className="hint">The winner keeps its own ID; the loser moves to Recycle bin. You&apos;ll be able to override individual fields in the next step.</p>
            <div className="dup-picker" data-testid="dup-picker">
              {active.profiles.map((p, i) => (
                <label key={`${p.kind}-${p.id}`} className={`dup-choice ${winnerIdx === i ? "on" : ""}`} data-testid={`dup-choice-${i}`}>
                  <input type="radio" checked={winnerIdx === i} onChange={() => setWinnerIdx(i)} />
                  <div>
                    <b>{p.name}</b>
                    <small>{p.kind === "contact" ? "Voter profile" : "Public login"} · {p.id}</small>
                  </div>
                </label>
              ))}
            </div>
            <div className="bo-drawer-actions">
              <button className="outline" onClick={() => setActive(null)}>Cancel</button>
              {active.profiles.filter((_, i) => i !== winnerIdx).map((loser, i) => (
                <button key={`${loser.kind}-${loser.id}`} className="primary" data-testid={`dup-preview-${i}`} disabled={busy}
                        onClick={() => openPreview(active.mobile, active.profiles[winnerIdx], loser)}>
                  <Eye size={13} /> Preview merge → drop <b>{loser.name}</b>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: field diff */}
      {preview && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div className="dup-preview" data-testid="dup-preview">
            <div className="bo-drawer-head">
              <b><Eye size={14} /> Merge preview · {preview.mobile}</b>
              <button className="icon-btn" data-testid="dup-preview-close" onClick={() => setPreview(null)}>×</button>
            </div>
            <div className="dup-preview-heads">
              <div className="dup-head dup-head-win"><small>Winner (kept)</small><b>{preview.winner.name}</b></div>
              <div className="dup-head-arrow"><ArrowRight size={14} /></div>
              <div className="dup-head dup-head-lose"><small>Loser (trashed)</small><b>{preview.loser.name}</b></div>
            </div>

            {Object.keys(preview.reassign_counts || {}).length > 0 && (
              <div className="dup-reassign" data-testid="dup-reassign">
                <b>Will be reassigned:</b>
                {Object.entries(preview.reassign_counts).map(([k, v]) => (
                  <span key={k} className="dup-reassign-chip">{v} {k.replace(/_/g, " ")}</span>
                ))}
              </div>
            )}

            {preview.diff.length === 0 ? (
              <p className="hint" style={{ padding: 20, textAlign: "center" }}>No mergeable field differences — nothing to override.</p>
            ) : (
              <table className="dup-diff" data-testid="dup-diff">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Winner value</th>
                    <th>Loser value</th>
                    <th style={{ width: 220 }}>Keep</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.diff.map((d) => (
                    <tr key={d.field} className={`dup-diff-row dup-diff-${d.status}`} data-testid={`dup-diff-${d.field}`}>
                      <td><code>{d.field}</code>{d.status === "conflict" && <span className="dup-conflict-tag">CONFLICT</span>}</td>
                      <td>{renderVal(d.winner_value)}</td>
                      <td>{renderVal(d.loser_value)}</td>
                      <td>
                        <div className="dup-diff-choices">
                          <label>
                            <input type="radio" name={d.field} checked={preview.choices[d.field] === "winner"} onChange={() => setPreview((p) => ({ ...p, choices: { ...p.choices, [d.field]: "winner" } }))} data-testid={`dup-choice-${d.field}-winner`} />
                            <span>Winner</span>
                          </label>
                          <label>
                            <input type="radio" name={d.field} checked={preview.choices[d.field] === "loser"} onChange={() => setPreview((p) => ({ ...p, choices: { ...p.choices, [d.field]: "loser" } }))} data-testid={`dup-choice-${d.field}-loser`} />
                            <span>Loser</span>
                          </label>
                          <label>
                            <input type="radio" name={d.field} checked={preview.choices[d.field] === "skip"} onChange={() => setPreview((p) => ({ ...p, choices: { ...p.choices, [d.field]: "skip" } }))} data-testid={`dup-choice-${d.field}-skip`} />
                            <span>Skip</span>
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="bo-drawer-actions">
              <button className="outline" data-testid="dup-preview-back" onClick={() => { const w = preview.winner; const l = preview.loser; const m = preview.mobile; setPreview(null); setActive({ mobile: m, profiles: [w, l].map((p) => ({ kind: p.kind || (p.contact_id ? "contact" : "public_user"), id: p.contact_id || p.user_id, name: p.name })) }); }}>
                <ArrowLeft size={13} /> Back
              </button>
              <button className="primary" data-testid="dup-merge-confirm" disabled={busy} onClick={doMerge}>
                <Check size={13} /> Confirm merge
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
